// Profile view tracking with daily deduplication
// Only increments when employer actually views EMT profile
import { db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp, runTransaction, collection, getDocs, query, orderBy, limit } from "firebase/firestore";

export interface ProfileViewRecord {
  id: string;
  emtId: string;
  employerOrgId?: string;
  employerAuthUid?: string;
  createdAt: any;
}

function getDbOrNull() {
  return db ?? null;
}

/**
 * Record a profile view (with daily deduplication)
 * Only increments counter if this employer hasn't viewed this EMT today
 * Called ONLY from employer profile view page
 */
export async function recordProfileView(params: {
  emtId: string;
  employerOrgId: string;
  employerAuthUid: string;
}): Promise<void> {
  const { emtId, employerOrgId, employerAuthUid } = params;
  const firestoreDb = getDbOrNull();
  if (!firestoreDb) {
    console.warn("[recordProfileView] Firestore not initialized; skipping view telemetry");
    return;
  }

  try {
    // Create deterministic daily dedupe doc ID: YYYYMMDD format
    const today = new Date();
    const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
    const viewId = `${employerOrgId}__${emtId}__${dateStr}`;

    // Only write the views subdoc. Do not update publicProfiles/{emtId} here — employers are
    // not allowed to update that doc in rules; that update caused permission-denied and aborted
    // the whole transaction (including the view row).
    await runTransaction(firestoreDb, async (transaction) => {
      const viewRef = doc(firestoreDb, "publicProfiles", emtId, "views", viewId);
      const viewSnap = await transaction.get(viewRef);

      if (!viewSnap.exists()) {
        transaction.set(viewRef, {
          emtId,
          employerOrgId,
          employerAuthUid,
          createdAt: serverTimestamp(),
        });
        if (import.meta.env.DEV) {
          console.log(`[recordProfileView] Recorded view for ${emtId} by org ${employerOrgId}`);
        }
      } else if (import.meta.env.DEV) {
        console.log(`[recordProfileView] View already recorded today for ${emtId} by org ${employerOrgId}`);
      }
    });
  } catch (err) {
    // Telemetry only — never throw to callers
    console.warn("[recordProfileView] Failed to record view:", err);
  }
}

/**
 * Get profile views count for an EMT (from publicProfiles doc)
 */
export async function getProfileViewsCount(emtId: string): Promise<number> {
  const firestoreDb = getDbOrNull();
  if (!firestoreDb) return 0;
  try {
    const ref = doc(firestoreDb, "publicProfiles", emtId);
    const snap = await getDoc(ref);
    return snap.exists() ? (snap.data()?.profileViewsCount ?? 0) : 0;
  } catch (err) {
    console.warn("[getProfileViewsCount] Error:", err);
    return 0;
  }
}

/**
 * List recent profile views for an EMT (own view records)
 * EMT can read publicProfiles/{emtId}/views per Firestore rules
 */
export async function listProfileViews(
  emtId: string,
  limitN = 30,
  opts?: { throwOnError?: boolean }
): Promise<ProfileViewRecord[]> {
  const firestoreDb = getDbOrNull();
  if (!firestoreDb) return [];
  try {
    const viewsRef = collection(firestoreDb, "publicProfiles", emtId, "views");
    const q = query(viewsRef, orderBy("createdAt", "desc"), limit(limitN));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as Omit<ProfileViewRecord, "id">),
    }));
  } catch (err) {
    console.warn("[listProfileViews] Error:", err);
    // throwOnError: the caller shows "couldn't load" instead of "No profile views yet".
    if (opts?.throwOnError) throw err;
    return [];
  }
}

